(() => {
  const SELECTORS = {
    root: '[data-rep-event]',
    productJson: '[data-rep-product-json]',
    optionGroup: '[data-rep-option]',
    optionInput: '[data-rep-option-input]',
    choice: '[data-rep-choice]',
    choicePrice: '[data-rep-choice-price]',
    saving: '[data-rep-saving]',
    variantId: '[data-rep-variant-id]',
    quantity: '[data-rep-quantity]',
    formQuantity: '[data-rep-form-quantity]',
    submit: '[data-rep-submit]',
    submitCopy: '[data-rep-submit-copy]',
    submitSeparator: '[data-rep-submit-separator]',
    total: '[data-rep-total]',
    summary: '[data-rep-summary]',
    summaryQuantity: '[data-rep-summary-quantity]',
    mainImage: '[data-rep-main-image]',
    thumbnail: '[data-rep-thumbnail]',
    format: '[data-rep-format]',
    inventory: '[data-rep-inventory]',
    inventoryCopy: '[data-rep-inventory-copy]',
  };

  function init(root) {
    if (root.dataset.repInitialized === 'true') return;

    const jsonElement = root.querySelector(SELECTORS.productJson);
    if (!jsonElement) return;

    let product;
    try {
      product = JSON.parse(jsonElement.textContent);
    } catch (error) {
      console.error('[REVIBRAS Event] No se pudo leer la información del producto.', error);
      return;
    }

    root.dataset.repInitialized = 'true';

    const variants = product.variants || [];
    const optionGroups = [...root.querySelectorAll(SELECTORS.optionGroup)];
    const variantInput = root.querySelector(SELECTORS.variantId);
    const quantityInput = root.querySelector(SELECTORS.quantity);
    const formQuantity = root.querySelector(SELECTORS.formQuantity);
    const submitButton = root.querySelector(SELECTORS.submit);
    const submitCopy = root.querySelector(SELECTORS.submitCopy);
    const submitSeparator = root.querySelector(SELECTORS.submitSeparator);
    const total = root.querySelector(SELECTORS.total);
    const summary = root.querySelector(SELECTORS.summary);
    const summaryQuantity = root.querySelector(SELECTORS.summaryQuantity);
    const mainImage = root.querySelector(SELECTORS.mainImage);
    const formatLabel = root.querySelector(SELECTORS.format);
    const inventory = root.querySelector(SELECTORS.inventory);
    const inventoryCopy = root.querySelector(SELECTORS.inventoryCopy);
    const inventoryThreshold = Number.parseInt(root.dataset.inventoryThreshold, 10) || 10;
    const currency = product.currency || root.dataset.currency || 'CLP';
    const money = new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    });

    let selectedVariant =
      variants.find((variant) => String(variant.id) === String(variantInput?.value)) || variants[0] || null;

    const getSelectedOptions = () =>
      optionGroups.map((group) => group.querySelector(`${SELECTORS.optionInput}:checked`)?.value || null);

    const findVariant = (options) =>
      variants.find(
        (variant) =>
          variant.options.length === options.length &&
          variant.options.every((value, index) => value === options[index])
      );

    const findVariantForChoice = (group, choiceValue) => {
      const options = getSelectedOptions();
      options[Number(group.dataset.optionPosition) - 1] = choiceValue;
      return findVariant(options);
    };

    const showVariantImage = (variant) => {
      if (!mainImage || !variant?.image || mainImage.src === variant.image) return;

      mainImage.classList.add('is-changing');
      mainImage.src = variant.image;
      mainImage.removeAttribute('srcset');
      mainImage.alt = variant.imageAlt || product.title || '';

      const matchingThumbnail = [...root.querySelectorAll(SELECTORS.thumbnail)].find(
        (thumbnail) => thumbnail.dataset.imageSrc === variant.image
      );
      root.querySelectorAll(SELECTORS.thumbnail).forEach((thumbnail) => {
        const isActive = thumbnail === matchingThumbnail;
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.setAttribute('aria-pressed', String(isActive));
      });

      requestAnimationFrame(() => mainImage.classList.remove('is-changing'));
    };

    const updateTicketChoices = () => {
      const ticketGroup = optionGroups.find((group) => group.dataset.optionKind === 'ticket');
      if (!ticketGroup) return;

      const ticketChoices = [...ticketGroup.querySelectorAll(SELECTORS.choice)];
      const baseChoice = ticketChoices.find((choice) =>
        /^(general|individual|1\s*(persona|entrada|ticket))/i.test(choice.dataset.value.trim())
      );
      const baseVariant = baseChoice ? findVariantForChoice(ticketGroup, baseChoice.dataset.value) : null;

      ticketChoices.forEach((choice) => {
        const input = choice.querySelector(SELECTORS.optionInput);
        const priceElement = choice.querySelector(SELECTORS.choicePrice);
        const savingElement = choice.querySelector(SELECTORS.saving);
        const prospectiveVariant = findVariantForChoice(ticketGroup, choice.dataset.value);

        if (priceElement && prospectiveVariant) priceElement.textContent = money.format(prospectiveVariant.price / 100);

        if (savingElement) {
          const countMatch = choice.dataset.value.match(/\b([2-9])\s*(?:x|personas?|entradas?|tickets?)/i);
          const packCount = countMatch ? Number(countMatch[1]) : 1;
          const saving =
            baseVariant && prospectiveVariant && packCount > 1
              ? baseVariant.price * packCount - prospectiveVariant.price
              : 0;

          savingElement.hidden = saving <= 0;
          if (saving > 0) savingElement.textContent = `AHORRA ${money.format(saving / 100)}`;
        }

        if (input && prospectiveVariant && !input.checked) input.disabled = !prospectiveVariant.available;
      });
    };

    const updateChoiceAvailability = () => {
      optionGroups.forEach((group) => {
        group.querySelectorAll(SELECTORS.choice).forEach((choice) => {
          const input = choice.querySelector(SELECTORS.optionInput);
          const prospectiveVariant = findVariantForChoice(group, choice.dataset.value);
          if (!input || !prospectiveVariant || input.checked) return;
          input.disabled = !prospectiveVariant.available;
        });
      });
    };

    const updateUrl = (variant) => {
      if (!variant || !window.history?.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    const updateInventory = (variant, isAvailable) => {
      if (!inventory || !inventoryCopy) return;

      const isTracked = variant?.inventoryManagement === 'shopify';
      if (!isTracked) {
        inventory.hidden = true;
        return;
      }

      const quantity = Math.max(0, Number.parseInt(variant.inventoryQuantity, 10) || 0);
      const continuesSelling = variant.inventoryPolicy === 'continue';
      const isSoldOut = !isAvailable && !continuesSelling;

      inventory.hidden = false;
      inventory.classList.toggle('is-low', quantity > 0 && quantity <= inventoryThreshold);
      inventory.classList.toggle('is-out', isSoldOut);

      if (isSoldOut) {
        inventoryCopy.textContent = root.dataset.soldOutLabel;
      } else if (quantity === 1) {
        inventoryCopy.textContent = root.dataset.inventorySingularLabel;
      } else if (quantity > 1) {
        inventoryCopy.textContent = root.dataset.inventoryPluralLabel.replace('[cantidad]', String(quantity));
      } else {
        inventoryCopy.textContent = root.dataset.inventoryOpenLabel;
      }
    };

    const updatePurchase = ({ updateHistory = true } = {}) => {
      const options = getSelectedOptions();
      const exactVariant = optionGroups.length ? findVariant(options) : selectedVariant;
      if (exactVariant) selectedVariant = exactVariant;

      const quantity = Math.max(1, Math.min(99, Number.parseInt(quantityInput?.value, 10) || 1));
      const isAvailable = Boolean(selectedVariant?.available && exactVariant !== undefined);

      if (quantityInput) quantityInput.value = quantity;
      if (formQuantity) formQuantity.value = quantity;

      if (variantInput && selectedVariant) {
        const changed = String(variantInput.value) !== String(selectedVariant.id);
        variantInput.value = selectedVariant.id;
        variantInput.disabled = false;
        if (changed) variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (summary) {
        const readableOptions = options.filter((value) => value && value !== 'Default Title');
        summary.textContent = readableOptions.length ? readableOptions.join(' · ') : product.title;
      }

      const ticketGroup = optionGroups.find((group) => group.dataset.optionKind === 'ticket');
      const ticketValue = ticketGroup?.querySelector(`${SELECTORS.optionInput}:checked`)?.value || 'ENTRADA';
      if (summaryQuantity) summaryQuantity.textContent = `${quantity} × ${ticketValue}`;

      if (total && selectedVariant) total.textContent = money.format((selectedVariant.price * quantity) / 100);

      if (submitButton) submitButton.disabled = !isAvailable;
      if (submitCopy) {
        submitCopy.textContent = isAvailable ? root.dataset.buttonLabel : root.dataset.soldOutLabel;
      }
      if (submitSeparator) submitSeparator.hidden = !isAvailable;
      if (total) total.hidden = !isAvailable;

      updateInventory(selectedVariant, isAvailable);
      updateChoiceAvailability();
      updateTicketChoices();
      showVariantImage(selectedVariant);
      if (updateHistory && selectedVariant) updateUrl(selectedVariant);
    };

    const hidePastDates = () => {
      if (root.dataset.hidePastDates !== 'true') return;

      const today = root.dataset.today;
      const dateGroup = optionGroups.find((group) => group.dataset.optionKind === 'date');
      if (!today || !dateGroup) return;

      const sortableDate = (value) => {
        const [year, month, day] = value.split('-').map(Number);
        return year * 10000 + month * 100 + day;
      };
      const todayValue = sortableDate(today);
      const datedChoices = [...dateGroup.querySelectorAll('[data-event-date]')];
      const futureChoices = datedChoices.filter((choice) => {
        const input = choice.querySelector(SELECTORS.optionInput);
        return sortableDate(choice.dataset.eventDate) >= todayValue && !input?.disabled;
      });

      if (!futureChoices.length) return;

      datedChoices.forEach((choice) => {
        choice.hidden = sortableDate(choice.dataset.eventDate) < todayValue;
      });

      const checkedChoice = datedChoices.find((choice) => choice.querySelector(`${SELECTORS.optionInput}:checked`));
      if (checkedChoice?.hidden || !checkedChoice) {
        const nextInput = futureChoices[0].querySelector(SELECTORS.optionInput);
        if (nextInput) nextInput.checked = true;
      }

      const visibleCount = datedChoices.filter((choice) => !choice.hidden).length;
      if (formatLabel) {
        formatLabel.textContent = visibleCount > 1 ? root.dataset.tourLabel : root.dataset.singleDateLabel;
      }
    };

    root.querySelectorAll(SELECTORS.optionInput).forEach((input) => {
      input.addEventListener('change', () => updatePurchase());
    });

    root.querySelector('[data-rep-quantity-minus]')?.addEventListener('click', () => {
      if (!quantityInput) return;
      quantityInput.value = Math.max(1, (Number.parseInt(quantityInput.value, 10) || 1) - 1);
      updatePurchase({ updateHistory: false });
    });

    root.querySelector('[data-rep-quantity-plus]')?.addEventListener('click', () => {
      if (!quantityInput) return;
      quantityInput.value = Math.min(99, (Number.parseInt(quantityInput.value, 10) || 1) + 1);
      updatePurchase({ updateHistory: false });
    });

    quantityInput?.addEventListener('change', () => updatePurchase({ updateHistory: false }));

    root.querySelectorAll(SELECTORS.thumbnail).forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        if (!mainImage || !thumbnail.dataset.imageSrc) return;
        mainImage.src = thumbnail.dataset.imageSrc;
        mainImage.removeAttribute('srcset');
        mainImage.alt = thumbnail.dataset.imageAlt || product.title || '';
        root.querySelectorAll(SELECTORS.thumbnail).forEach((item) => {
          const isActive = item === thumbnail;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-pressed', String(isActive));
        });
      });
    });

    hidePastDates();
    updatePurchase({ updateHistory: false });
  }

  const initAll = (scope = document) => {
    scope.querySelectorAll(SELECTORS.root).forEach(init);
  };

  initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
